import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { 
  Search, 
  Filter, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  Calendar,
  Smartphone,
  User,
  Clock,
  CheckCircle,
  Package
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RepairsList = () => {
  const { user } = useAuth();
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [technicianFilter, setTechnicianFilter] = useState('all');

  useEffect(() => {
    fetchRepairs();
  }, []);

  const fetchRepairs = async () => {
    try {
      const response = await axios.get(`${API}/repairs`);
      setRepairs(response.data);
    } catch (error) {
      console.error('Error fetching repairs:', error);
      toast.error('Erreur lors du chargement des réparations');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const searchParams = {
        search_term: searchTerm || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        technician: technicianFilter === 'all' ? undefined : technicianFilter
      };

      // Remove undefined values
      Object.keys(searchParams).forEach(key => 
        searchParams[key] === undefined && delete searchParams[key]
      );

      const response = await axios.post(`${API}/repairs/search`, searchParams);
      setRepairs(response.data);
    } catch (error) {
      console.error('Error searching repairs:', error);
      toast.error('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (repairId, newStatus) => {
    try {
      await axios.put(`${API}/repairs/${repairId}`, { status: newStatus });
      
      // Update local state
      setRepairs(repairs.map(repair => 
        repair.id === repairId ? { ...repair, status: newStatus } : repair
      ));
      
      const statusLabels = {
        'en_cours': 'En cours',
        'termine': 'Terminé',
        'recupere': 'Récupéré'
      };
      
      toast.success(`Statut changé: ${statusLabels[newStatus]}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDelete = async (repairId, repairNumber) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la réparation ${repairNumber} ?\n\nCette action est irréversible et supprimera toutes les données associées.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/repairs/${repairId}`);
      setRepairs(repairs.filter(repair => repair.id !== repairId));
      toast.success('Réparation supprimée avec succès');
    } catch (error) {
      console.error('Error deleting repair:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // ========== TICKETS PROFESSIONNELS ==========
  
  const handlePrintA4Professional = async (repairId, repairNumber) => {
    try {
      const response = await axios.get(`${API}/repairs/${repairId}/print/ticket-a4-professional`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });
      
      if (!response.data || response.data.size === 0) {
        throw new Error('Fichier PDF vide reçu du serveur');
      }
      
      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new window
      const newWindow = window.open(url, '_blank');
      
      // Cleanup blob URL after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
      
      if (!newWindow) {
        // If popup blocked, create download link
        const link = document.createElement('a');
        link.href = url;
        link.download = `ticket_a4_pro_${repairNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      toast.success('Ticket A4 Professionnel généré avec succès');
    } catch (error) {
      console.error('Error printing A4 professional ticket:', error);
      toast.error('Erreur lors de la génération du ticket A4 professionnel');
    }
  };

  const handlePrintTerraProfessional = async (repairId, repairNumber) => {
    try {
      const response = await axios.get(`${API}/repairs/${repairId}/print/ticket-terra-professional`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });
      
      if (!response.data || response.data.size === 0) {
        throw new Error('Fichier PDF vide reçu du serveur');
      }
      
      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new window
      const newWindow = window.open(url, '_blank');
      
      // Cleanup blob URL after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
      
      if (!newWindow) {
        // If popup blocked, create download link
        const link = document.createElement('a');
        link.href = url;
        link.download = `ticket_terra_pro_${repairNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      toast.success('Ticket Terra 72mm Professionnel généré avec succès');
    } catch (error) {
      console.error('Error printing Terra professional ticket:', error);
      toast.error('Erreur lors de la génération du ticket Terra professionnel');
    }
  };

  const handlePrintZebraEticket = async (repairId, repairNumber) => {
    try {
      const response = await axios.get(`${API}/repairs/${repairId}/print/eticket-zebra-57x32`, {
        responseType: 'blob',
        headers: {
          'Accept': 'image/png'
        }
      });
      
      if (!response.data || response.data.size === 0) {
        throw new Error('Fichier PNG vide reçu du serveur');
      }
      
      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new window
      const newWindow = window.open(url, '_blank');
      
      // Cleanup blob URL after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
      
      if (!newWindow) {
        // If popup blocked, create download link
        const link = document.createElement('a');
        link.href = url;
        link.download = `eticket_zebra_${repairNumber}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      toast.success('E-ticket Zebra 57x32mm généré avec succès');
    } catch (error) {
      console.error('Error printing Zebra e-ticket:', error);
      toast.error('Erreur lors de la génération de l\'e-ticket Zebra');
    }
  };


  const getStatusIcon = (status) => {
    switch (status) {
      case 'en_cours':
        return <Clock className="w-4 h-4" />;
      case 'termine':
        return <CheckCircle className="w-4 h-4" />;
      case 'recupere':
        return <Package className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'en_cours': { label: 'En cours', variant: 'secondary', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
      'termine': { label: 'Terminé', variant: 'secondary', className: 'bg-blue-100 text-blue-800 border-blue-200' },
      'recupere': { label: 'Récupéré', variant: 'secondary', className: 'bg-green-100 text-green-800 border-green-200' }
    };
    
    const statusInfo = statusMap[status] || { label: status, className: 'bg-gray-100 text-gray-800 border-gray-200' };
    
    return (
      <Badge className={`${statusInfo.className} border`}>
        <div className="flex items-center space-x-1">
          {getStatusIcon(status)}
          <span>{statusInfo.label}</span>
        </div>
      </Badge>
    );
  };

  const StatusSelector = ({ repair }) => {
    const statusMap = {
      'en_cours': { label: 'En cours', className: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' },
      'termine': { label: 'Terminé', className: 'bg-blue-100 text-blue-800 hover:bg-blue-200' },
      'recupere': { label: 'Récupéré', className: 'bg-green-100 text-green-800 hover:bg-green-200' }
    };

    const currentStatus = statusMap[repair.status] || { label: repair.status, className: 'bg-gray-100 text-gray-800' };

    return (
      <Select 
        value={repair.status} 
        onValueChange={(value) => handleStatusChange(repair.id, value)}
      >
        <SelectTrigger className={`w-36 h-8 ${currentStatus.className} border-none font-medium`}>
          <div className="flex items-center space-x-1">
            {getStatusIcon(repair.status)}
            <SelectValue />
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="en_cours">
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-yellow-600" />
              <span>En cours</span>
            </div>
          </SelectItem>
          <SelectItem value="termine">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-blue-600" />
              <span>Terminé</span>
            </div>
          </SelectItem>
          <SelectItem value="recupere">
            <div className="flex items-center space-x-2">
              <Package className="w-4 h-4 text-green-600" />
              <span>Récupéré</span>
            </div>
          </SelectItem>
        </SelectContent>
      </Select>
    );
  };

  const uniqueTechnicians = [...new Set(repairs.map(repair => repair.assigned_technician))];

  if (loading && repairs.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="repairs-list">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Réparations</h1>
          <p className="text-slate-600 mt-1">Gestion de toutes les réparations</p>
        </div>
        <Link to="/repairs/new">
          <Button 
            className="mt-4 sm:mt-0 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 btn-hover-lift"
            data-testid="add-repair-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle réparation
          </Button>
        </Link>
      </div>

      {/* Search and Filters */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Search className="w-5 h-5" />
            <span>Recherche et filtres</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <Input
                placeholder="Rechercher par client, numéro, appareil..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="en_cours">En cours</SelectItem>
                <SelectItem value="termine">Terminé</SelectItem>
                <SelectItem value="recupere">Récupéré</SelectItem>
              </SelectContent>
            </Select>
            <Select value={technicianFilter} onValueChange={setTechnicianFilter}>
              <SelectTrigger data-testid="technician-filter">
                <SelectValue placeholder="Technicien" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les techniciens</SelectItem>
                {uniqueTechnicians.filter(tech => tech && tech.trim()).map(tech => (
                  <SelectItem key={tech} value={tech}>{tech}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={handleSearch} 
            className="w-full sm:w-auto"
            disabled={loading}
            data-testid="search-button"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="spinner w-4 h-4 mr-2"></div>
                Recherche...
              </div>
            ) : (
              <>
                <Filter className="w-4 h-4 mr-2" />
                Rechercher
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Repairs List */}
      <div className="space-y-4">
        {repairs.length > 0 ? (
          repairs.map((repair, index) => (
            <Card key={repair.id} className="glass card-hover slide-up" style={{ animationDelay: `${index * 50}ms` }}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                  {/* Main Info */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center space-x-4">
                      <h3 className="text-lg font-semibold text-slate-900" data-testid={`repair-number-${index}`}>
                        {repair.repair_number}
                      </h3>
                      <StatusSelector repair={repair} />
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                      <div className="flex items-center space-x-2 text-slate-600">
                        <User className="w-4 h-4" />
                        <span>
                          <span className="font-medium">Client:</span> {repair.client_name}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-slate-600">
                        <Smartphone className="w-4 h-4" />
                        <span>
                          <span className="font-medium">Appareil:</span> {repair.device}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-slate-600">
                        <User className="w-4 h-4" />
                        <span>
                          <span className="font-medium">Technicien:</span> {repair.assigned_technician}
                        </span>
                      </div>
                    </div>

                    <div className="text-sm text-slate-600">
                      <p><span className="font-medium">Problème:</span> {repair.problem_description}</p>
                    </div>

                    <div className="flex items-center space-x-4 text-xs text-slate-500">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3" />
                        <span>Entrée: {new Date(repair.entry_date).toLocaleDateString('fr-FR')}</span>
                      </div>
                      {repair.estimated_exit_date && (
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-3 h-3" />
                          <span>Prévue: {new Date(repair.estimated_exit_date).toLocaleDateString('fr-FR')}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2 lg:flex-col lg:space-x-0 lg:space-y-2">
                    <Link to={`/repairs/edit/${repair.id}`}>
                      <Button variant="outline" size="sm" className="w-full" data-testid={`edit-repair-${index}`}>
                        <Edit className="w-4 h-4 mr-2" />
                        Modifier
                      </Button>
                    </Link>
                    
                    {/* ========== TICKETS PROFESSIONNELS ========== */}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePrintA4Professional(repair.id, repair.repair_number)}
                      className="w-full text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 border-indigo-200"
                      data-testid={`print-a4-pro-${index}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      A4 Pro (Client + Tech)
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePrintTerraProfessional(repair.id, repair.repair_number)}
                      className="w-full text-pink-600 hover:text-pink-700 hover:bg-pink-50 border-pink-200"
                      data-testid={`print-terra-pro-${index}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Terra 72mm Pro
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePrintZebraEticket(repair.id, repair.repair_number)}
                      className="w-full text-teal-600 hover:text-teal-700 hover:bg-teal-50 border-teal-200"
                      data-testid={`print-zebra-eticket-${index}`}
                    >
                      <Package className="w-4 h-4 mr-2" />
                      Zebra E-ticket 57x32
                    </Button>
                    
                    {user?.role === 'admin' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(repair.id, repair.repair_number)}
                        className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                        data-testid={`delete-repair-${index}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="glass">
            <CardContent className="text-center py-12">
              <Smartphone className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Aucune réparation trouvée</h3>
              <p className="text-slate-600 mb-6">
                {searchTerm || statusFilter !== 'all' || technicianFilter !== 'all' 
                  ? 'Aucune réparation ne correspond à vos critères de recherche.'
                  : 'Commencez par créer votre première réparation.'
                }
              </p>
              <Link to="/repairs/new">
                <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer une réparation
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Results Summary */}
      {repairs.length > 0 && (
        <Card className="glass">
          <CardContent className="p-4">
            <p className="text-sm text-slate-600 text-center">
              {repairs.length} réparation(s) trouvée(s)
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RepairsList;